import { either } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { useSelector } from 'react-redux';
import { SearchQuery } from 'modules/domains/segmentation';
import { queryBuilderSelector } from '../selectors';

const isObject = (value: unknown) =>
  value ? either.right(value as Record<string, unknown>) : either.left('not an object');

const hasKey = (key: string) => (value: Record<string, unknown>) =>
  Object.keys(value).includes(key) ? either.right(value) : either.left(`Missing key ${key}`);

const hasValue = (key: keyof SearchQuery) => (value: Record<string, unknown>) =>
  (value as SearchQuery)[key] ? either.right(value) : either.left(`Missing value for ${key}`);

const hasArrayValue = (key: keyof SearchQuery) => (value: Record<string, unknown>) =>
  Array.isArray((value as SearchQuery)[key])
    ? either.right((value as SearchQuery).searchQueries)
    : either.left(`${key} isn't an array`);

const isntEmptyArray = (value: unknown[]) =>
  value.length > 0 ? either.right(true) : either.left('Array is empty');

const isValidQuery = (value: unknown): value is SearchQuery =>
  pipe(
    value,
    isObject,
    either.chain(hasKey('combinator')),
    either.chain(hasKey('searchQueries')),
    either.chain(hasValue('combinator')),
    either.chain(hasArrayValue('searchQueries')),
    either.chain(isntEmptyArray),
    either.isRight,
  );

export const useQueryBuilderState = () => {
  const query = useSelector(queryBuilderSelector) as SearchQuery | undefined | Record<never, never>;

  return isValidQuery(query) ? query : undefined;
};
